import { EXPECTED_TERMINAL_DETAILS, WAFClearanceDetail } from './types';

export const WAF_CIRCUIT_FAILURE_THRESHOLD = 5;
export const WAF_CIRCUIT_COOLDOWN_MS = 60_000;

// One consecutive-failure counter per tab (module singleton): any success wipes the run, so the count compares
// straight to the threshold and the trip is latency-independent.
// WAF_CIRCUIT_COOLDOWN_MS is both the self-heal gap and the max spacing for two failures to share a run.
// While tripped the loop surrenders before any solve, so no success can wipe the run: the cooldown is the only
// escape, which is why a backward clock jump that future-dates the last failure must reset it, not pin it open.
let consecutiveFailureCount = 0;
let lastFailureAtMs = 0;

function resetRunIfStale(nowMs: number): void {
  if (consecutiveFailureCount > 0 && (nowMs - lastFailureAtMs >= WAF_CIRCUIT_COOLDOWN_MS || lastFailureAtMs > nowMs)) {
    consecutiveFailureCount = 0;
    lastFailureAtMs = 0;
  }
}

export const wafCircuitBreaker = {
  recordFailure(): void {
    const nowMs = Date.now();
    resetRunIfStale(nowMs);
    consecutiveFailureCount += 1;
    lastFailureAtMs = nowMs;
  },
  recordSuccess(): void {
    consecutiveFailureCount = 0;
    lastFailureAtMs = 0;
  },
  isTripped(): boolean {
    resetRunIfStale(Date.now());
    return consecutiveFailureCount >= WAF_CIRCUIT_FAILURE_THRESHOLD;
  },
};

export function TestOnlyResetWAFCircuitBreaker(): void {
  consecutiveFailureCount = 0;
  lastFailureAtMs = 0;
}

/**
 * Whether a non-solved clearance should count toward the tab circuit breaker. The user-cancel teardown and the
 * EXPECTED_TERMINAL_DETAILS floor are excluded: those are not recovery degradations, so counting them would trip
 * the generic circuit-open modal in place of the accurate per-detail one, and would scope the breaker off a
 * different population than the failure-ratio monitor.
 */
export function isCircuitCountableFailure(detail: WAFClearanceDetail | undefined): boolean {
  return detail === undefined || (detail !== WAFClearanceDetail.Cancelled && !EXPECTED_TERMINAL_DETAILS.has(detail));
}
